import { CommonModule } from '@angular/common';
import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router } from '@angular/router';

import { Constants } from '../../common';
import { DynamicHeight } from '../../helper';
import { DateFormatPipe } from '../../helper/pipes/date-format/date-format-pipe';
import {
  IAdminScreenDetail,
  IAdminScreenInformationSections
} from '../../models';
import { CommonService } from '../../services';
import { ScreenService } from '../../services/screen';
import { environment } from '../../../environments';

interface IDetailField {
  label: string;
  value: string;
}

interface IScreenSummary {
  screenId: number;
  screenName: string;
  screenImageUrl: string | null;
  operationalStatus: string;
  activeState: string;
  fillerAd: string;
  screenCode: string;
  businessId: string;
  deviceType: string;
  businessType: string;
  resolution: string;
  osVersion: string;
  playerVersion: string;
  registered: string;
  heartbeat: string;
  lastPing: string;
}

@Component({
  selector: 'app-screen-detail',
  imports: [CommonModule, MatButtonModule, MatIconModule, DynamicHeight],
  templateUrl: './screen-detail.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './screen-detail.component.scss',
})
export class ScreenDetailComponent implements OnInit {
  private readonly dateFormatPipe = new DateFormatPipe();

  screenDetail: IAdminScreenDetail | null = null;
  summary: IScreenSummary | null = null;
  mainDetails: IDetailField[] = [];
  systemDetails: IDetailField[] = [];
  hardwareDetails: IDetailField[] = [];
  connectivityDetails: IDetailField[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private screenService: ScreenService,
    public commonService: CommonService
  ) { }

  ngOnInit(): void {
    this.route.paramMap.subscribe((params) => {
      const screenId = Number(params.get('id'));
      if (Number.isFinite(screenId) && screenId > 0) {
        this.loadScreenDetail(screenId);
      }
    });
  }

  onBack() {
    this.router.navigate([Constants.APP_ROUTES.screensList]);
  }

  getStatusClass(status: string): string {
    switch (status.toLowerCase()) {
      case 'live':
        return 'status-live';
      case 'standby':
        return 'status-standby';
      case 'active':
        return 'status-active';
      case 'draft':
        return 'status-standby';
      case 'inactive':
      default:
        return 'status-inactive';
    }
  }

  private loadScreenDetail(id: number) {
    this.commonService.showSpinner = true;

    this.screenService.getBusinessScreenById(id).subscribe({
      next: (detail) => {
        this.screenDetail = detail;
        this.summary = this.buildSummary(detail);
        this.mainDetails = this.buildMainDetails(detail);
        const infoSections = this.extractInformationSections(detail);
        this.systemDetails = this.buildSystemDetails(infoSections);
        this.hardwareDetails = this.buildHardwareDetails(infoSections);
        this.connectivityDetails = this.buildConnectivityDetails(infoSections);
        this.commonService.showSpinner = false;
      },
      error: () => {
        this.screenDetail = null;
        this.summary = null;
        this.mainDetails = [];
        this.systemDetails = [];
        this.hardwareDetails = [];
        this.connectivityDetails = [];
        this.commonService.showSpinner = false;
      }
    });
  }

  private buildSummary(detail: IAdminScreenDetail): IScreenSummary {
    return {
      screenId: detail.id,
      screenName: this.asText(detail.name, `Screen ${detail.id}`),
      screenImageUrl: this.resolveImageUrl(detail.thumbnail),
      operationalStatus: this.resolveOperationalStatus(detail.status),
      activeState: detail.is_active ? 'ACTIVE' : 'INACTIVE',
      fillerAd: detail.is_admin_ad_enabled ? 'ENABLED' : 'DISABLED',
      screenCode: this.formatValue(detail.code),
      businessId: this.formatValue(detail.business),
      deviceType: this.formatValue(detail.type),
      businessType: this.formatValue(detail.business_type),
      resolution: this.formatResolution(detail.resolution_width, detail.resolution_height),
      osVersion: this.formatValue(detail.os_version),
      playerVersion: this.formatValue(detail.player_version),
      registered: this.formatBoolean(detail.is_registered),
      heartbeat: this.formatBoolean(detail.is_heartbeat_enabled),
      lastPing: this.formatDateValue(detail.ping_at)
    };
  }

  private buildMainDetails(detail: IAdminScreenDetail): IDetailField[] {
    return [
      { label: 'Screen ID', value: this.formatValue(detail.id) },
      { label: 'Code', value: this.formatValue(detail.code) },
      { label: 'Business ID', value: this.formatValue(detail.business) },
      { label: 'Business Type', value: this.formatValue(detail.business_type) },
      { label: 'Device Type', value: this.formatValue(detail.type) },
      { label: 'Operating System', value: this.formatValue(detail.os) },
      { label: 'OS Version', value: this.formatValue(detail.os_version) },
      { label: 'Resolution', value: this.formatResolution(detail.resolution_width, detail.resolution_height) },
      { label: 'Heartbeat', value: this.formatBoolean(detail.is_heartbeat_enabled) },
      { label: 'Registered', value: this.formatBoolean(detail.is_registered) },
      { label: 'Last Ping', value: this.formatDateValue(detail.ping_at) },
      { label: 'Updated At', value: this.formatDateValue(detail.updated_at) },
      { label: 'Location', value: this.formatValue(detail.location) },
      { label: 'Address', value: this.formatValue(detail.location_address) }
    ];
  }

  private extractInformationSections(detail: IAdminScreenDetail): IAdminScreenInformationSections {
    const sections = detail.all_informations?.nameValuePairs;
    return {
      system: sections?.system?.nameValuePairs ?? null,
      hardware: sections?.hardware?.nameValuePairs ?? null,
      connectivity: sections?.connectivity?.nameValuePairs ?? null
    };
  }

  private buildSystemDetails(info: IAdminScreenInformationSections): IDetailField[] {
    const system = info.system;
    return [
      { label: 'Brand', value: this.formatValue(system?.brand) },
      { label: 'Manufacturer', value: this.formatValue(system?.manufacturer) },
      { label: 'Model', value: this.formatValue(system?.model) },
      { label: 'Device', value: this.formatValue(system?.device) },
      { label: 'OS', value: this.formatValue(system?.os) },
      { label: 'OS Version', value: this.formatValue(system?.os_version) },
      { label: 'SDK Version', value: this.formatValue(system?.sdk_version) },
      { label: 'Fingerprint', value: this.formatValue(system?.fingerprint) }
    ];
  }

  private buildHardwareDetails(info: IAdminScreenInformationSections): IDetailField[] {
    const hardware = info.hardware;
    const memory = hardware?.memory?.nameValuePairs;
    const display = hardware?.display?.nameValuePairs;
    const processor = hardware?.processor?.nameValuePairs;
    const audioCodecs = hardware?.audio_codecs?.nameValuePairs;
    const videoCodecs = hardware?.video_codecs?.nameValuePairs;

    return [
      { label: 'GPU', value: this.formatValue(hardware?.gpu) },
      { label: 'RAM', value: this.formatValue(memory?.ram) },
      { label: 'Storage', value: this.formatValue(memory?.storage) },
      { label: 'Display Resolution', value: this.formatValue(display?.resolution) },
      { label: 'Pixel Density', value: this.formatValue(display?.pixel_density) },
      { label: 'Processor Cores', value: this.formatValue(processor?.cores) },
      { label: 'Processor Config', value: this.formatArray(processor?.configuration) },
      { label: 'Audio Codecs', value: this.formatArray(audioCodecs?.supported?.values) },
      { label: 'Video Codecs', value: this.formatArray(videoCodecs?.supported?.values) }
    ];
  }

  private buildConnectivityDetails(info: IAdminScreenInformationSections): IDetailField[] {
    const connectivity = info.connectivity;
    const wifi = connectivity?.wifi?.nameValuePairs;
    const bluetooth = connectivity?.bluetooth?.nameValuePairs;

    return [
      { label: 'Wi-Fi Supported', value: this.formatBoolean(wifi?.supported) },
      { label: 'Wi-Fi Direct', value: this.formatBoolean(wifi?.wifi_direct) },
      { label: 'Ethernet', value: this.formatBoolean(connectivity?.ethernet) },
      { label: 'Bluetooth', value: this.formatBoolean(bluetooth?.supported) }
    ];
  }

  private resolveOperationalStatus(statusValue: unknown): string {
    const rawStatus = this.asText(statusValue, '');
    const normalizedStatus = rawStatus.toLowerCase();

    if (['live', 'standby', 'inactive', 'active', 'draft'].includes(normalizedStatus)) {
      return rawStatus.toUpperCase();
    }

    return 'INACTIVE';
  }

  private formatValue(value: unknown): string {
    const text = this.asNullableText(value);
    if (text !== null) {
      return text;
    }

    if (typeof value === 'number' && Number.isFinite(value)) {
      return value.toString();
    }

    return 'Not available';
  }

  private formatBoolean(value: boolean | null | undefined): string {
    if (value === true) {
      return 'Yes';
    }
    if (value === false) {
      return 'No';
    }
    return 'Not available';
  }

  private formatArray(values: string[] | null | undefined): string {
    if (Array.isArray(values) && values.length > 0) {
      return values.join(', ');
    }
    return 'Not available';
  }

  private formatResolution(width: number | null, height: number | null): string {
    if (width && height) {
      return `${width} x ${height}`;
    }
    return 'Not available';
  }

  private formatDateValue(value: string | null | undefined): string {
    if (!value) {
      return 'Not available';
    }

    return this.dateFormatPipe.transform(value) ?? 'Not available';
  }

  private asText(value: unknown, fallback: string): string {
    return this.asNullableText(value) ?? fallback;
  }

  private asNullableText(value: unknown): string | null {
    if (typeof value === 'string') {
      const trimmed = value.trim();
      return trimmed ? trimmed : null;
    }
    return null;
  }

  private resolveImageUrl(value: string | null | undefined): string | null {
    const text = this.asNullableText(value);
    if (!text) {
      return null;
    }

    if (/^(https?:)?\/\//i.test(text) || text.startsWith('data:') || text.startsWith('blob:')) {
      return text;
    }

    try {
      return new URL(text, environment.zignFlixUrl).toString();
    } catch {
      return text;
    }
  }
}
